// Card 47, the listening watch (R2657, ui-map-interface.md §17.3.47,
// v2-the-deep.md §11.3 and §13 "Discovery"; mockup round 031 card 47).
//
// The sonar display is a waterfall: the bearing across, the time down, and
// every sound the hydrophone holds is a trace that slides down the strip,
// so the bearing rate of a contact is a slope the eye reads before any
// figure. Here an own anchored Hark keeps the watch: the last
// `window_map_sec` map seconds of what it heard, one trace a contact, the
// designation of card 41 at its head and the brightness its nearness.
//
// The traces are the contacts the client already received, inside the
// sonar of the buoy (`SeaObjectSpec.sonar_px`), sampled each `sample_sec`
// wall seconds. The strip shows nothing the server withheld; it gives the
// reader what the map cannot: whether a contact draws left or right.
//
// **Where it draws.** The strip is markup: the card of an own anchored
// Hark in the water peek carries it as its body
// (`setSeaObjectBody` of `ui/cards/seaobjects.ts`), drawn when the card
// opens. On the map the piece draws one dashed ring round each buoy whose
// watch holds a sound now, and nothing else.
//
// **WITH THE HARKS.** The piece draws while the picture holds an own
// anchored Hark. A world with `sapper` off holds none, and draws the
// chart of today.
//
// **COST.** A live piece: one dashed ring a buoy that hears; a sample a
// contact in reach each `sample_sec`; the strip on open alone.

import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartReading, ChartSinks, Piece } from '../context';
import { chart as S } from '../../../gen/strings';
import { CHART } from '../../../gen/presentation';
import { SeaObjectKind } from '../../../gen/proto/aquaphobia';
import { factionColor } from '../../../world';
import { bearingDeg } from '../../../render/bearing';
import { hex } from '../../../render/color';
import { esc } from '../../../ui/esc';
import { setSeaObjectBody, type SeaObjectView } from '../../../ui/cards/seaobjects';
import { contactBook } from './contact-book';
import { CHART_INK, inkOf } from './shared';

/** One sound of the watch. */
export interface WatchSample {
  /** The wall ms of the sample. */
  atMs: number;
  /** The contact's fleet id and faction. */
  id: number;
  faction: number;
  /** The true bearing from the buoy, degrees from north, clockwise. */
  bearing: number;
  /** 1 at the buoy, 0 at the edge of its reach. */
  near: number;
}

/** Is this sea object an own anchored Hark, the listener of a
 *  watch and of a picket line (card 49)? */
export function ownPicket(o: { own: boolean; armed: boolean; kind: SeaObjectKind }): boolean {
  return o.own && o.armed && o.kind === SeaObjectKind.SEA_OBJECT_KIND_PICKET_BUOY;
}

const pad3 = (n: number): string => String(Math.round(n) % 360).padStart(3, '0');

/**
 * The strip of one watch as markup: the head line, then an SVG of the
 * bearing across and the age down. `windowMs` is the wall length of the
 * window, `nowMs` the top of the strip.
 */
export function watchHtml(samples: readonly WatchSample[], nowMs: number, windowMs: number, reading: ChartReading,
  minutes: number): string {
  const f = CHART.pieces.listening_watch.fig;
  const hist = reading === 'historical';
  const w = f.strip_w, h = f.strip_h, gx = 0, gy = 0;
  const pale = hex(CHART_INK.pale), faint = hex(CHART_INK.faint);
  // The strip writes in the caption step of the chrome; the card kit owns
  // no rule for it, so the two tokens ride the element.
  let svg = `<svg class="lw-strip" width="${w}" height="${h + f.axis_h}" viewBox="0 0 ${w} ${h + f.axis_h}" `
    + 'style="display:block;font:var(--t-cap)" aria-hidden="true">'
    + `<rect x="${gx}" y="${gy}" width="${w}" height="${h}" fill="${hex(CHART_INK.deep)}" fill-opacity="${f.ground_alpha}"/>`;
  for (const b of [0, 90, 180, 270, 360]) {
    const x = gx + (b / 360) * w;
    svg += `<line x1="${x}" y1="${gy}" x2="${x}" y2="${gy + h}" stroke="${pale}" stroke-opacity="${f.grid_alpha}" stroke-width="${f.rule_w}"/>`
      + `<text x="${Math.min(w - f.axis_inset_px, Math.max(f.axis_inset_px, x))}" y="${gy + h + f.axis_h - f.axis_lift_px}" fill="${faint}" text-anchor="middle">${pad3(b)}</text>`;
  }
  const newest = new Map<number, WatchSample>();
  for (const s of samples) {
    const age = (nowMs - s.atMs) / Math.max(1, windowMs);
    if (age < 0 || age > 1) continue;
    const a = (f.trace_alpha_floor + f.trace_alpha_age * (1 - age)) * (f.trace_near_floor + f.trace_near_k * s.near);
    const x = gx + (s.bearing / 360) * w, y = gy + age * h;
    svg += `<rect x="${(x - f.trace_px / 2).toFixed(1)}" y="${y.toFixed(1)}" width="${f.trace_px}" height="${f.trace_h}" `
      + `fill="${hex(factionColor(s.faction))}" fill-opacity="${a.toFixed(2)}"/>`;
    const had = newest.get(s.id);
    if (!had || had.atMs < s.atMs) newest.set(s.id, s);
  }
  for (const s of [...newest.values()].sort((p, q) => p.id - q.id)) {
    const n = contactBook.numberOf(s.id);
    if (n === 0) continue;
    svg += `<text x="${((s.bearing / 360) * w).toFixed(1)}" y="${gy + f.label_dy}" fill="${hex(factionColor(s.faction))}" text-anchor="middle">${esc(S.watch_designation({ n }))}</text>`;
  }
  svg += '</svg>';
  const head = hist ? S.watch_head_historical : S.watch_head({ min: minutes });
  // The sheet sets the two lines: the modern capitals at the cap step with
  // the cap tracking, the lower-case words of `.hist` at the small step
  // (R2161, R3247).
  const rd = hist ? ' hist' : '';
  const quiet = newest.size === 0 ? `<div class="lw-quiet${rd}">${esc(hist ? S.watch_quiet_historical : S.watch_quiet)}</div>` : '';
  return `<div class="lw"><div class="lw-head${rd}">${esc(head)}</div>${svg}${quiet}</div>`;
}

export class ListeningWatchPiece implements Piece {
  readonly id = 'listening_watch' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  /** The watch is today's: the film hides it. */
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hello'] as const;

  /** The sounds of each own buoy, by its id. The tests read them. */
  readonly watches = new Map<number, WatchSample[]>();
  private lastMs = -Infinity;
  private windowMs = 0;
  private reading: ChartReading = 'modern';
  private minutes = 0;

  constructor() {
    // The card of an own anchored Hark carries the strip.
    setSeaObjectBody((o) => this.bodyOf(o));
  }

  visible(ctx: ChartContext): boolean {
    return ctx.state.seaObjects.list.some(ownPicket);
  }

  rebuild(_ctx: ChartContext): void {
    this.watches.clear();
    this.lastMs = -Infinity;
  }

  update(ctx: ChartContext, _frame: FrameInfo): void {
    const fig = ctx.cfg.pieces.listening_watch.fig;
    const st = ctx.state;
    const now = Date.now();
    this.reading = ctx.reading;
    this.minutes = Math.round(fig.window_map_sec / 60);
    this.windowMs = (fig.window_map_sec / Math.max(1e-9, st.tacticalScale)) * 1000;
    if (now - this.lastMs < fig.sample_sec * 1000) return;
    this.lastMs = now;
    const reach = st.seaObject(SeaObjectKind.SEA_OBJECT_KIND_PICKET_BUOY)?.sonarPx ?? 0;
    const fleets = ctx.scene.fleets?.chartFleets() ?? [];
    const keep = new Set<number>();
    for (const o of st.seaObjects.list) {
      if (!ownPicket(o) || !(reach > 0)) continue;
      keep.add(o.id);
      let w = this.watches.get(o.id);
      if (!w) { w = []; this.watches.set(o.id, w); }
      for (const f of fleets) {
        if (f.own || f.faction === st.faction) continue;
        const d = Math.hypot(f.x - o.x, f.y - o.y);
        if (d > reach) continue;
        w.push({ atMs: now, id: f.id, faction: f.faction, bearing: bearingDeg(o.x, o.y, f.x, f.y), near: 1 - d / reach });
      }
      const cut = now - this.windowMs;
      let i = 0;
      while (i < w.length && w[i].atMs < cut) i++;
      if (i > 0) w.splice(0, i);
    }
    for (const id of this.watches.keys()) if (!keep.has(id)) this.watches.delete(id);
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const t = ctx.cfg.pieces.listening_watch;
    const r = t[ctx.reading];
    const ink = inkOf(r.ink, CHART_INK.pale);
    const now = Date.now();
    for (const o of ctx.state.seaObjects.list) {
      const w = this.watches.get(o.id);
      if (!w || w.length === 0 || now - w[w.length - 1].atMs > t.fig.sample_sec * 2000) continue;
      sinks.circles.dashed(o.x, o.y, t.fig.ring_px, r.width, t.fig.ring_dashes, t.fig.ring_dash_fill, ink, r.alpha);
    }
  }

  /** The strip of an own anchored Hark, or '' for any other object. */
  bodyOf(o: SeaObjectView): string {
    if (!ownPicket(o)) return '';
    return watchHtml(this.watches.get(o.id) ?? [], Date.now(), this.windowMs, this.reading, this.minutes);
  }
}

export const listeningWatch = new ListeningWatchPiece();
